import { axiosServerNodeServices } from 'utils/axios';

export type UpdateContactMessagingBody = {
  contactId: number;
  phone: string;
  marketingConsentLevel: number;
};

export type UpdateContactMessagingResponse = {
  result: boolean;
  message?: string;
};

export type MarketingConsentErrorCode = 'bad_request' | 'unauthorized' | 'not_found' | 'server' | 'unknown';

export class MarketingConsentUpdateError extends Error {
  constructor(
    message: string,
    public readonly code: MarketingConsentErrorCode,
    public readonly serverPayload?: unknown,
  ) {
    super(message);
    this.name = 'MarketingConsentUpdateError';
  }
}

export function normalizePhoneForChatUserApi(phone: string): string {
  const digits = String(phone ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('0') && digits.length >= 9) {
    return `972${digits.slice(1)}`;
  }
  return digits;
}

function extractMessageFromPayload(data: unknown): string {
  if (data == null) return '';
  if (typeof data === 'string') return data.trim();
  if (typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const m = o.message ?? o.Message ?? o.error;
    if (typeof m === 'string' && m.trim()) return m.trim();
  }
  return '';
}

function codeFromHttpStatus(status: number): MarketingConsentErrorCode {
  if (status === 400) return 'bad_request';
  if (status === 401) return 'unauthorized';
  if (status === 404) return 'not_found';
  if (status >= 500) return 'server';
  return 'unknown';
}

/**
 * עדכון MarketingConsentLevel ב-ChatUsers (JWT + X-Company-Id דרך axiosServerNodeServices).
 * משתמש ב-validateStatus כדי לקבל קוד HTTP מלא (ה-interceptor הכללי מסיר אותו בשגיאות).
 */
export async function updateContactMessagingStatus(
  body: UpdateContactMessagingBody,
): Promise<UpdateContactMessagingResponse> {
  const contactId = Number(body.contactId);
  const normalizedPhone = normalizePhoneForChatUserApi(body.phone);
  const level = Number(body.marketingConsentLevel);

  if (!Number.isFinite(contactId) || contactId <= 0) {
    throw new MarketingConsentUpdateError('Invalid contactId', 'bad_request');
  }
  if (!normalizedPhone) {
    throw new MarketingConsentUpdateError('Missing phone', 'bad_request');
  }
  if (![0, 1, 2].includes(level)) {
    throw new MarketingConsentUpdateError('Invalid marketingConsentLevel', 'bad_request');
  }

  try {
    const response = await axiosServerNodeServices.post<UpdateContactMessagingResponse>(
      '/api/services/updateContactMessagingStatus',
      {
        contactId,
        phone: normalizedPhone,
        marketingConsentLevel: level,
      },
      { validateStatus: () => true },
    );

    const { status, data } = response;

    if (status === 200 && data && data.result !== false) {
      return data;
    }
    if (status === 200 && data?.result === false) {
      throw new MarketingConsentUpdateError(
        extractMessageFromPayload(data) || 'Update rejected',
        'unknown',
        data,
      );
    }

    const serverMsg = extractMessageFromPayload(data);
    const code = codeFromHttpStatus(status);
    throw new MarketingConsentUpdateError(serverMsg || `HTTP ${status}`, code, data);
  } catch (e) {
    if (e instanceof MarketingConsentUpdateError) throw e;
    throw new MarketingConsentUpdateError(
      e instanceof Error ? e.message : 'Network error',
      'unknown',
    );
  }
}

export type BulkMarketingConsentTarget = {
  contactId: number;
  phone: string;
};

export type BulkMarketingConsentResult = {
  successIds: number[];
  failedIds: number[];
  firstError: unknown;
};

/** מספר בקשות מקבילות — מגן על השרת בעדכון של מאות אנשי קשר. */
const BULK_CONSENT_CHUNK_SIZE = 8;

/**
 * עדכון מרוכז של MarketingConsentLevel לרשימת אנשי קשר.
 * מריץ במנות כדי לא להציף את השרת, וממשיך גם כאשר חלק מהעדכונים נכשלים.
 */
export async function bulkUpdateMarketingConsent(
  targets: BulkMarketingConsentTarget[],
  marketingConsentLevel: number,
): Promise<BulkMarketingConsentResult> {
  const successIds: number[] = [];
  const failedIds: number[] = [];
  let firstError: unknown = null;

  for (let i = 0; i < targets.length; i += BULK_CONSENT_CHUNK_SIZE) {
    const chunk = targets.slice(i, i + BULK_CONSENT_CHUNK_SIZE);

    const settled = await Promise.allSettled(
      chunk.map((target) =>
        updateContactMessagingStatus({
          contactId: target.contactId,
          phone: target.phone,
          marketingConsentLevel,
        }),
      ),
    );

    settled.forEach((outcome, index) => {
      const target = chunk[index];
      if (outcome.status === 'fulfilled') {
        successIds.push(target.contactId);
      } else {
        failedIds.push(target.contactId);
        if (firstError === null) firstError = outcome.reason;
      }
    });
  }

  return { successIds, failedIds, firstError };
}
